'use client';

import { useRef, useState, type ChangeEvent } from 'react';
import {
  APPOINTMENT_TEXTS,
  bookingBody,
  confirmationText,
  EMPTY_CONTACT,
  effectiveMode,
  initialMode,
  knownQuoteToken,
  missingContactFields,
  postBooking,
  submitLabel,
  type BookingConfirmationTexts,
  type ContactFields,
} from '@/app/lib/guideChat/appointment';
import type { PhoneCta } from '@/app/lib/guideChat/types';

export interface AppointmentSlot {
  id: string;
  date: string;
  time: string;
  value: number;
  label: string;
  humanLabel: string;
  available?: boolean;
}

export interface AppointmentDate {
  date: string;
  label: string;
}

export interface AppointmentCardData {
  kind: string;
  primary: boolean;
  title: string;
  message: string;
  nonAnswerReason: string | null;
  nonAnswerReasonLabel: string | null;
  product: string;
  dates?: AppointmentDate[];
  date?: string | null;
  slots: AppointmentSlot[];
  handover: Record<string, unknown>;
  transmissionNotice?: string;
  alternative?: { kind: string; message: string } | null;
  // Business hours: offer an immediate callback next to the slot picker.
  callNow?: boolean;
  callNowLabel?: string | null;
  callNowMessage?: string | null;
  slotPickerLabel?: string;
  phone?: PhoneCta | null;
  confirmation?: BookingConfirmationTexts;
}

interface TransmissionCall {
  step: string;
  url: string;
  status: number;
  ok: boolean;
}

interface Booking {
  reference: string;
  status: string;
  slot: AppointmentSlot | null;
  adviserBrief: string;
  quoteToken: string | null;
  callNow?: boolean;
  attachedToExistingQuote?: boolean;
  transmittedToAdviser: boolean;
  transmission?: { target?: string; calls?: TransmissionCall[] };
}

const inputCls =
  'border border-gray-300 rounded px-2 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-400';
const labelCls = 'block text-xs text-gray-500 mb-0.5';

// `compact` hides the technical transmission details meant for the admin debug page.
export default function AppointmentCard({ card, compact = false }: { card: AppointmentCardData; compact?: boolean }) {
  const dates = card.dates ?? [];
  const [mode, setMode] = useState<'callNow' | 'slot'>(initialMode(card));
  const [date, setDate] = useState<string>(card.date ?? dates[0]?.date ?? '');
  const [slots, setSlots] = useState<AppointmentSlot[]>(card.slots);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotId, setSlotId] = useState<string | null>(card.slots.find((s) => s.available !== false)?.id ?? null);
  const [contact, setContact] = useState<ContactFields>(EMPTY_CONTACT);
  const [status, setStatus] = useState<'form' | 'loading' | 'done'>('form');
  const [error, setError] = useState<string | null>(null);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const slotsRequest = useRef(0);

  // Like the landing modal: picking another date reloads that day's grid from the CRM (taken slots stay visible, greyed).
  const changeDate = (next: string) => {
    setDate(next);
    setError(null);
    const request = ++slotsRequest.current;
    if (!next || next === card.date) {
      setSlots(card.slots);
      setSlotId(card.slots.find((s) => s.available !== false)?.id ?? null);
      setSlotsLoading(false);
      return;
    }
    setSlotsLoading(true);
    fetch(`/api/poc-guide-chat/appointment-slots?date=${next}`, { cache: 'no-store' })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);
        if (request !== slotsRequest.current) return;
        const list: AppointmentSlot[] = data.slots ?? [];
        setSlots(list);
        setSlotId(list.find((s) => s.available !== false)?.id ?? null);
      })
      .catch((e) => {
        if (request !== slotsRequest.current) return;
        setSlots([]);
        setSlotId(null);
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (request === slotsRequest.current) setSlotsLoading(false);
      });
  };

  // §8.4: with no bookable slot the adviser calls back without an appointment time.
  const canBookWithoutSlot = !!card.alternative && slots.length === 0;
  const bookingMode = effectiveMode(mode, canBookWithoutSlot);
  const update = (field: keyof ContactFields) => (e: ChangeEvent<HTMLInputElement>) =>
    setContact((c) => ({ ...c, [field]: e.target.value }));

  async function submit() {
    if (missingContactFields(contact).length > 0) {
      setError(APPOINTMENT_TEXTS.missingContact);
      return;
    }
    if (bookingMode === 'slot' && !slotId) {
      setError(APPOINTMENT_TEXTS.missingSlot);
      return;
    }
    setStatus('loading');
    setError(null);
    const result = await postBooking(
      bookingBody({
        mode: bookingMode,
        slotId,
        contact,
        product: card.product,
        handover: card.handover,
        quoteToken: knownQuoteToken(),
      }),
    );
    if (!result.ok) {
      setError(result.error);
      setStatus('form');
      return;
    }
    setBooking((result.booking as Booking | null) ?? null);
    setConfirmation(confirmationText(bookingMode, result.confirmation, card.confirmation));
    setStatus('done');
  }

  if (status === 'done') {
    const target = booking?.transmission?.target;
    return (
      <div className="mt-1 w-full bg-white border border-emerald-200 rounded-xl p-3 shadow-sm">
        <div className="text-sm font-semibold text-emerald-800 mb-1">Rappel enregistré chez Réassurez-moi</div>
        {booking && (
          <p className="text-sm text-gray-700">
            {booking.slot?.humanLabel ?? (booking.callNow ? 'Rappel immédiat' : 'Rappel sans créneau')} — référence{' '}
            <span className="font-mono">{booking.reference}</span>
          </p>
        )}
        <p className="mt-2 text-xs text-gray-700">{confirmation}</p>
        {!compact && booking && (
          <p className="mt-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
            Un vrai rappel a été créé{target ? ` sur ${target}` : ''} : une tâche CRM
            {booking.attachedToExistingQuote ? ' sur le devis existant' : ' sur une fiche de rappel'} et les confirmations
            e-mail et SMS. Un conseiller rappellera réellement ce numéro (statut{' '}
            <span className="font-mono">{booking.status}</span>
            {booking.quoteToken && (
              <>
                , devis <span className="font-mono">{booking.quoteToken}</span>
              </>
            )}
            ).
          </p>
        )}
        {!compact && booking?.transmission?.calls && (
          <ul className="mt-2 space-y-0.5">
            {booking.transmission.calls.map((call, i) => (
              <li key={`${call.step}-${i}`} className="text-xs text-gray-500">
                <span className={call.ok ? 'text-emerald-700' : 'text-red-600'}>{call.ok ? '✓' : '✗'}</span>{' '}
                <span className="font-mono">{call.step}</span> → HTTP {call.status}
              </li>
            ))}
          </ul>
        )}
        {!compact && booking && (
          <details className="mt-2">
            <summary className="text-xs text-gray-500 hover:text-gray-800 underline cursor-pointer">
              Contexte transmis au conseiller
            </summary>
            <pre className="mt-1 bg-gray-900 text-gray-100 rounded p-2 text-xs whitespace-pre-wrap">{booking.adviserBrief}</pre>
          </details>
        )}
      </div>
    );
  }

  return (
    <div className="mt-1 w-full bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <span className="rounded border px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800 border-emerald-200">
          Prise de RDV
        </span>
        {!compact && card.nonAnswerReasonLabel && <span className="text-xs text-gray-400">{card.nonAnswerReasonLabel}</span>}
      </div>
      <div className="text-sm font-semibold text-gray-800">{card.title}</div>
      <p className="text-sm text-gray-600 mt-0.5">{card.message}</p>

      {card.phone && (
        <p className="mt-2 text-sm">
          <a href={card.phone.href} className="font-semibold text-emerald-700 underline hover:text-emerald-800">
            {card.phone.label} : {card.phone.number}
          </a>
        </p>
      )}

      {card.callNow && (
        <div className="mt-3 grid grid-cols-2 gap-1.5" role="radiogroup">
          {(['callNow', 'slot'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={mode === option}
              onClick={() => {
                setMode(option);
                setError(null);
              }}
              className={`rounded-lg px-2 py-1.5 text-xs font-medium border ${
                mode === option ? 'bg-emerald-600 text-white border-emerald-600' : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {option === 'callNow'
                ? card.callNowLabel || APPOINTMENT_TEXTS.callNow
                : card.slotPickerLabel || APPOINTMENT_TEXTS.pickSlot}
            </button>
          ))}
        </div>
      )}

      {mode === 'callNow' && card.callNowMessage && <p className="mt-2 text-xs text-gray-600">{card.callNowMessage}</p>}

      {mode === 'slot' && dates.length > 0 && (
        <div className="mt-3">
          <label className={labelCls}>Date</label>
          <select value={date} onChange={(e) => changeDate(e.target.value)} className={`${inputCls} sm:w-auto capitalize`}>
            {dates.map((d) => (
              <option key={d.date} value={d.date}>
                {d.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {mode === 'slot' && (
        <div className="mt-2">
          <label className={labelCls}>Heure</label>
          {slotsLoading ? (
            <p className="text-xs text-gray-400 animate-pulse">Chargement des créneaux…</p>
          ) : slots.length === 0 ? (
            <p className="text-xs text-gray-500">
              {canBookWithoutSlot ? card.alternative?.message : 'Aucun créneau ce jour-là, choisissez une autre date.'}
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {slots.map((slot) => {
                const taken = slot.available === false;
                return (
                  <button
                    key={slot.id}
                    type="button"
                    disabled={taken}
                    onClick={() => setSlotId(slot.id)}
                    title={taken ? 'Créneau déjà réservé' : undefined}
                    className={`rounded px-2.5 py-1 text-xs border ${
                      slotId === slot.id
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : taken
                          ? 'bg-gray-100 border-gray-200 text-gray-300 line-through cursor-not-allowed'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {slot.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls}>{APPOINTMENT_TEXTS.firstName}</label>
          <input className={inputCls} value={contact.firstName} onChange={update('firstName')} autoComplete="given-name" />
        </div>
        <div>
          <label className={labelCls}>{APPOINTMENT_TEXTS.lastName}</label>
          <input className={inputCls} value={contact.lastName} onChange={update('lastName')} autoComplete="family-name" />
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls}>{APPOINTMENT_TEXTS.phone}</label>
          <input
            className={inputCls}
            type="tel"
            value={contact.phone}
            onChange={update('phone')}
            placeholder="06 12 34 56 78"
            autoComplete="tel"
          />
        </div>
        <div>
          <label className={labelCls}>{APPOINTMENT_TEXTS.email}</label>
          <input
            className={inputCls}
            type="email"
            value={contact.email}
            onChange={update('email')}
            placeholder="prenom.nom@exemple.fr"
            autoComplete="email"
          />
        </div>
      </div>

      {!compact && card.transmissionNotice && (
        <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
          {card.transmissionNotice}
        </p>
      )}

      {error && <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error}</div>}

      <button
        type="button"
        onClick={submit}
        disabled={status === 'loading' || (bookingMode === 'slot' && slotId === null)}
        className="mt-3 w-full rounded-lg bg-emerald-600 text-white font-medium py-2 disabled:opacity-50 hover:bg-emerald-700"
      >
        {status === 'loading' ? APPOINTMENT_TEXTS.sending : submitLabel(bookingMode, card.callNowLabel)}
      </button>
    </div>
  );
}
